import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';
import { NotificationProvider } from './components/NotificationProvider';

// Pages
import HomePage from './pages/HomePage';
import SafetyMapPage from './pages/SafetyMapPage';
import FixItForwardPage from './pages/FixItForwardPage';
import EngageHubPage from './pages/EngageHubPage';
import ResourcesPage from './pages/ResourcesPage';
import GetInvolvedPage from './pages/GetInvolvedPage';
import AboutPage from './pages/AboutPage';
import AuthPage from './pages/AuthPage';
import CommunityNewsPage from './pages/CommunityNewsPage';
import SpotlightsPage from './pages/SpotlightsPage';
import BusinessDirectoryPage from './pages/BusinessDirectoryPage';
import BusinessProfilePage from './pages/BusinessProfilePage';
import MyBusinessPage from './pages/MyBusinessPage';
import ResidentDashboard from './pages/ResidentDashboard';
import CarpoolPage from './pages/CarpoolPage';
import BecomeDriverPage from './pages/BecomeDriverPage';
import CarpoolRoutePage from './pages/CarpoolRoutePage';
import MyRoutesPage from './pages/MyRoutesPage';
import MyRidesPage from './pages/MyRidesPage';
import PollsPage from './pages/PollsPage';
import GalleryPage from './pages/GalleryPage';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminAnnouncements from './pages/admin/AdminAnnouncements';
import AdminEvents from './pages/admin/AdminEvents';
import AdminNews from './pages/admin/AdminNews';
import AdminSpotlights from './pages/admin/AdminSpotlights';
import AdminBusinesses from './pages/admin/AdminBusinesses';
import AdminVolunteers from './pages/admin/AdminVolunteers';
import AdminMessages from './pages/admin/AdminMessages';
import AdminIssues from './pages/admin/AdminIssues';
import AdminResources from './pages/admin/AdminResources';
import AdminSafetyPins from './pages/admin/AdminSafetyPins';
import AdminSettings from './pages/admin/AdminSettings';
import AdminManagement from './pages/admin/AdminManagement';
import AdminCarpoolDrivers from './pages/admin/AdminCarpoolDrivers';
import AdminCarpoolRoutes from './pages/admin/AdminCarpoolRoutes';
import AdminRegistrations from './pages/admin/AdminRegistrations';
import AdminTownHall from './pages/admin/AdminTownHall';
import AdminLiveStreams from './pages/admin/AdminLiveStreams';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminPolls from './pages/admin/AdminPolls';
import AdminGallery from './pages/admin/AdminGallery';
import AdminPopups from './pages/admin/AdminPopups';
import AdminHomepagePhotos from './pages/admin/AdminHomepagePhotos';
import AdminSiteContent from './pages/admin/AdminSiteContent';

// Public Pages
import TownHallPage from './pages/TownHallPage';
import MessagesPage from './pages/MessagesPage';

// Components
import Layout from './components/Layout';
import AdminLayout from './components/AdminLayout';
import GlobalPopupModal from './components/GlobalPopupModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// PLP Logo URL
export const PLP_LOGO = "https://customer-assets.emergentagent.com/job_safer-together/artifacts/xyq4w6nf_PLP%20LOGO%20KILLARNEY%20.jpg";

// Robyn Lyons Photo
export const ROBYN_PHOTO = "https://customer-assets.emergentagent.com/job_safer-together/artifacts/8rbblzsh_ROBYN%20LYONS%20.jpg";

// Killarney Community Photos
export const COMMUNITY_PHOTOS = {
  main: "https://customer-assets.emergentagent.com/job_safer-together/artifacts/owxudpdl_killarney%20commmunity%20.png",
  group: "https://customer-assets.emergentagent.com/job_safer-together/artifacts/f6fihb37_Screenshot%202026-03-01%20at%201.18.12%E2%80%AFPM.png",
  family: "https://customer-assets.emergentagent.com/job_safer-together/artifacts/grfuh144_Screenshot%202026-03-01%20at%201.18.04%E2%80%AFPM.png",
  gathering: "https://customer-assets.emergentagent.com/job_safer-together/artifacts/mfa77ewq_Screenshot%202026-03-01%20at%201.17.49%E2%80%AFPM.png"
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    try {
      const storedToken = localStorage.getItem('token');
      // Basic JWT format validation
      if (storedToken && storedToken.split('.').length === 3) {
        return storedToken;
      }
      return null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route for Admin
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  
  if (!user || !user.is_admin) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <NotificationProvider>
          <Routes>
            {/* Public Routes */}
            <Route element={<Layout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/safety-map" element={<SafetyMapPage />} />
              <Route path="/fix-it-forward" element={<FixItForwardPage />} />
              <Route path="/engage" element={<EngageHubPage />} />
              <Route path="/resources" element={<ResourcesPage />} />
              <Route path="/get-involved" element={<GetInvolvedPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/news" element={<CommunityNewsPage />} />
              <Route path="/spotlights" element={<SpotlightsPage />} />
              <Route path="/directory" element={<BusinessDirectoryPage />} />
              <Route path="/directory/:businessId" element={<BusinessProfilePage />} />
              <Route path="/my-business" element={<MyBusinessPage />} />
              <Route path="/dashboard" element={<ResidentDashboard />} />
              <Route path="/carpool" element={<CarpoolPage />} />
              <Route path="/carpool/become-driver" element={<BecomeDriverPage />} />
              <Route path="/carpool/route/:routeId" element={<CarpoolRoutePage />} />
              <Route path="/carpool/my-routes" element={<MyRoutesPage />} />
              <Route path="/carpool/my-rides" element={<MyRidesPage />} />
              <Route path="/townhall" element={<TownHallPage />} />
              <Route path="/messages" element={<MessagesPage />} />
              <Route path="/polls" element={<PollsPage />} />
              <Route path="/gallery" element={<GalleryPage />} />
            </Route>
            
            {/* Admin Routes */}
            <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="announcements" element={<AdminAnnouncements />} />
              <Route path="events" element={<AdminEvents />} />
              <Route path="news" element={<AdminNews />} />
              <Route path="spotlights" element={<AdminSpotlights />} />
              <Route path="businesses" element={<AdminBusinesses />} />
              <Route path="volunteers" element={<AdminVolunteers />} />
              <Route path="registrations" element={<AdminRegistrations />} />
              <Route path="messages" element={<AdminMessages />} />
              <Route path="issues" element={<AdminIssues />} />
              <Route path="safety-pins" element={<AdminSafetyPins />} />
              <Route path="resources" element={<AdminResources />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="admins" element={<AdminManagement />} />
              <Route path="carpool-drivers" element={<AdminCarpoolDrivers />} />
              <Route path="carpool-routes" element={<AdminCarpoolRoutes />} />
              <Route path="townhall" element={<AdminTownHall />} />
              <Route path="streams" element={<AdminLiveStreams />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="polls" element={<AdminPolls />} />
              <Route path="gallery" element={<AdminGallery />} />
              <Route path="popups" element={<AdminPopups />} />
              <Route path="homepage-photos" element={<AdminHomepagePhotos />} />
              <Route path="site-content" element={<AdminSiteContent />} />
            </Route>
            
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
          <GlobalPopupModal />
        </NotificationProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
